import React, { useState, useEffect, useRef } from "react";
import { Box, Button, styled, keyframes } from "@mui/material";

/* ── Animations ───────────────────────────────────────────── */
const tickPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(220,38,38,0.25), 0 2px 16px rgba(0,0,0,0.4); }
  50%       { box-shadow: 0 0 18px rgba(220,38,38,0.5),  0 2px 20px rgba(0,0,0,0.5); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Outer bar ────────────────────────────────────────────── */
const TopBar = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "90px",
    left: 0,
    right: 0,
    zIndex: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 20px",
    background: "linear-gradient(135deg, #0d0618 0%, #12082a 60%, #0a0415 100%)",
    borderBottom: "1px solid rgba(220,38,38,0.18)",
    borderTop: "1px solid rgba(255,255,255,0.04)",
    backdropFilter: "blur(20px)",
    animation: `${slideDown} 0.4s ease both`,

    /* subtle grid texture */
    backgroundImage: `
    linear-gradient(135deg, #0d0618 0%, #12082a 60%, #0a0415 100%),
    repeating-linear-gradient(0deg, transparent, transparent 24px, rgba(255,255,255,0.015) 24px, rgba(255,255,255,0.015) 25px),
    repeating-linear-gradient(90deg, transparent, transparent 24px, rgba(255,255,255,0.015) 24px, rgba(255,255,255,0.015) 25px)
  `,

    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        gap: "10px",
        padding: "12px 16px",
    },
}));

/* ── Individual widget card ───────────────────────────────── */
const Widget = styled(Box)(({ running }) => ({
    position: "relative",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "14px",
    padding: "12px 18px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    minWidth: "200px",
    overflow: "hidden",
    transition: "border-color 0.3s, box-shadow 0.3s",
    ...(running === "true" && {
        borderColor: "rgba(220,38,38,0.45)",
        animation: `${glowPulse} 2s ease-in-out infinite`,
    }),

    /* corner accent line */
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "15%", right: "15%", height: "1px",
        background: running === "true"
            ? "linear-gradient(to right, transparent, rgba(220,38,38,0.6), transparent)"
            : "linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)",
        transition: "background 0.3s",
    },
}));

/* ── Label above the time ─────────────────────────────────── */
const Label = styled("div")({
    fontSize: "0.6rem",
    fontFamily: "'DM Mono', 'Fira Code', monospace",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.3)",
    lineHeight: 1,
});

/* ── The big time digits ──────────────────────────────────── */
const TimeDisplay = styled("div")(({ running }) => ({
    fontFamily: "'DM Mono', 'Fira Code', 'Courier New', monospace",
    fontSize: "1.75rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: running === "true" ? "#f87171" : "#e2e8f0",
    lineHeight: 1,
    transition: "color 0.3s",
    ...(running === "true" && {
        animation: `${tickPulse} 1s step-start infinite`,
    }),
}));

/* ── Button row ───────────────────────────────────────────── */
const BtnRow = styled(Box)({
    display: "flex",
    gap: "6px",
    justifyContent: "center",
});

/* ── Slim action buttons ──────────────────────────────────── */
const ActionBtn = styled(Button)({
    fontFamily: "'DM Mono', 'Fira Code', monospace",
    fontSize: "0.62rem",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: "4px 10px",
    minWidth: 0,
    borderRadius: "6px",
    border: "1px solid",
    lineHeight: 1.4,
    transition: "all 0.18s ease",
});

/* ── Status dot ───────────────────────────────────────────── */
const StatusDot = styled("span")(({ active }) => ({
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    marginRight: "6px",
    background: active === "true" ? "#22c55e" : "rgba(255,255,255,0.2)",
    boxShadow: active === "true" ? "0 0 6px #22c55e" : "none",
    transition: "all 0.3s",
    verticalAlign: "middle",
    ...(active === "true" && {
        animation: `${tickPulse} 1s ease-in-out infinite`,
    }),
}));

/* ── Day / date badge ─────────────────────────────────────── */
const DateBadge = styled("div")({
    fontFamily: "'DM Mono', 'Fira Code', monospace",
    fontSize: "0.6rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.25)",
});

/* ══════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════ */
export default function LectureTimer() {

    /* ── Stopwatch ─────────────────────────────────────────── */
    const [time, setTime] = useState(0);
    const [running, setRunning] = useState(false);
    const timerRef = useRef(null);

    const startStopwatch = () => {
        if (!running) {
            setRunning(true);
            timerRef.current = setInterval(() => setTime(p => p + 1), 1000);
        }
    };

    const stopStopwatch = () => {
        setRunning(false);
        clearInterval(timerRef.current);
    };

    const resetStopwatch = () => {
        setRunning(false);
        clearInterval(timerRef.current);
        setTime(0);
    };

    const fmt = (s) => {
        const h = String(Math.floor(s / 3600)).padStart(2, "0");
        const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
        const sec = String(s % 60).padStart(2, "0");
        return `${h}:${m}:${sec}`;
    };

    /* ── Live clock ─────────────────────────────────────────── */
    const [clock, setClock] = useState({ time: "", date: "", day: "" });

    useEffect(() => {
        const tick = () => {
            const now = new Date();
            setClock({
                time: now.toLocaleTimeString("en-US", { hour12: false }),
                date: now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
                day: now.toLocaleDateString("en-US", { weekday: "long" }),
            });
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    /* ── Elapsed label ──────────────────────────────────────── */
    const elapsedLabel = (() => {
        if (time === 0) return "not started";
        if (running) return "recording";
        return "paused";
    })();

    return (
        <TopBar>

            {/* ── Stopwatch widget ──────────────────────────── */}
            <Widget running={running.toString()}>
                <Label>
                    <StatusDot active={running.toString()} />
                    lecture timer · {elapsedLabel}
                </Label>

                <TimeDisplay running={running.toString()}>
                    {fmt(time)}
                </TimeDisplay>

                <BtnRow>
                    <ActionBtn
                        onClick={startStopwatch}
                        disabled={running}
                        sx={{
                            borderColor: running ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.5)",
                            color: running ? "rgba(34,197,94,0.3)" : "#22c55e",
                            bgcolor: running ? "transparent" : "rgba(34,197,94,0.08)",
                            "&:hover:not(:disabled)": { bgcolor: "rgba(34,197,94,0.15)", borderColor: "#22c55e" },
                        }}
                    >
                        Start
                    </ActionBtn>

                    <ActionBtn
                        onClick={stopStopwatch}
                        disabled={!running}
                        sx={{
                            borderColor: !running ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.5)",
                            color: !running ? "rgba(239,68,68,0.3)" : "#ef4444",
                            bgcolor: !running ? "transparent" : "rgba(239,68,68,0.08)",
                            "&:hover:not(:disabled)": { bgcolor: "rgba(239,68,68,0.15)", borderColor: "#ef4444" },
                        }}
                    >
                        Stop
                    </ActionBtn>

                    <ActionBtn
                        onClick={resetStopwatch}
                        sx={{
                            borderColor: "rgba(148,163,184,0.25)",
                            color: "rgba(148,163,184,0.5)",
                            bgcolor: "transparent",
                            "&:hover": { bgcolor: "rgba(148,163,184,0.08)", borderColor: "rgba(148,163,184,0.5)", color: "#94a3b8" },
                        }}
                    >
                        Reset
                    </ActionBtn>
                </BtnRow>
            </Widget>

            {/* ── Clock widget ──────────────────────────────── */}
            <Widget>
                <Label>local time</Label>
                <TimeDisplay>{clock.time}</TimeDisplay>
                <DateBadge>{clock.day} · {clock.date}</DateBadge>
            </Widget>

        </TopBar>
    );
}
